import cx from 'clsx';
import { forwardRef, type ReactElement, type ReactNode, useReducer } from 'react';
import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
} from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Description, Error, Input, Label } from '../form-elements';
import { EyeIcon } from './icons/eye';
import { EyeHiddenIcon } from './icons/eye-hidden';
import styles from './text-field.module.css';

export interface TextFieldComponentProps extends AriaTextFieldProps {
  label: string | ReactNode;
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string) | ReactNode;
  placeholder: string;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldComponentProps>(function TextField(
  {
    label,
    description,
    errorMessage,
    placeholder,
    className,
    type,
    multiple,
    isOptional,
    isOptionalText,
    ...props
  },
  ref
): ReactElement {
  const { formatMessage } = useIntl();
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(state => !state, false);

  const resolvedErrorMessage =
    errorMessage ??
    (props.isRequired
      ? ({ validationDetails }: ValidationResult) =>
          validationDetails.valueMissing ? formatMessage({ id: 'field-error-required' }) : ''
      : undefined);

  return (
    <AriaTextField
      className={cx(className, styles.container)}
      type={!isPasswordVisible ? type : 'text'}
      {...props}
    >
      {Boolean(label) && (
        <Label>
          {label}
          {Boolean(isOptional) && [
            ' ',
            isOptionalText || formatMessage({ id: 'form-elements-optional' }),
          ]}
        </Label>
      )}
      <span className={styles['input-container']}>
        <Input multiple={multiple} placeholder={placeholder} ref={ref} />

        {type === 'password' && (
          <button
            aria-label={
              isPasswordVisible
                ? formatMessage({ id: 'a11y-buttons-hide-password-aria-label' })
                : formatMessage({ id: 'a11y-buttons-show-password-aria-label' })
            }
            className={styles.icon}
            onClick={togglePasswordVisibility}
            type="button"
          >
            {isPasswordVisible ? <EyeHiddenIcon aria-hidden /> : <EyeIcon aria-hidden />}
          </button>
        )}
      </span>
      <Error>{resolvedErrorMessage}</Error>
      {Boolean(description) && <Description>{description}</Description>}
    </AriaTextField>
  );
});
